import { cancelBackspaceAtContentStart } from "@shared/unified-tiptap/util/cancel-backspace-at-node-start"
import type { CardAttrsBase } from "@shared/unified-tiptap/util/card"
import ObjectId from "@shared/util/object-id-gen"
import { Editor, mergeAttributes, Node } from "@tiptap/core"
import { GapCursor } from "@tiptap/pm/gapcursor"
import { Fragment, Slice } from "@tiptap/pm/model"
import { NodeSelection, Plugin, TextSelection } from "@tiptap/pm/state"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { isMoveDrop, wholeNodeOf } from "../../util/clipboard"
import { renderBooleanAsData, renderJsonAsData, renderStringAsData } from "../../util/render-data"
import { CardExtensionComponent } from "./card-component"

export interface CardExtensionAttrs extends CardAttrsBase {
  // `color` is placed on the CardTitle because it needs to be accessible there.
}

export const CARD_VALUE_SCHEMA = "block+"

/**
 * The ordinary lesson card (a cardTitle plus block content). Group "card" is shared
 * with the other card types (e.g. cardGoogleClassroom): checks for "any card" use
 * `type.isInGroup("card")`, while titledCard-only logic checks the name.
 */
export const ExtensionTitledCard = cardExtensionFactory(
  "titledCard",
  "cardTitle " + CARD_VALUE_SCHEMA,
)

/**
 * Untitled version of the ordinary lesson card (just block content). Group "card" is shared
 * with the other card types (e.g. cardGoogleClassroom): checks for "any card" use
 * `type.isInGroup("card")`, while untitledCard-only logic checks the name.
 */
export const ExtensionUntitledCard = cardExtensionFactory("untitledCard", CARD_VALUE_SCHEMA)

/**
 * Factory to produce a plain card extension. Used for titledCard and untitledCard,
 * which both have block content; they differ in that titledCard has a cardTitle
 * as its first child while untitledCard does not.
 *
 * NOTE: When creating a new extension from this factory, add it to the `.node-${nodeName}` CSS selectors
 * in card.module.css, since we don't create those programmatically.
 */
function cardExtensionFactory(nodeName: string, content: string) {
  return Node.create({
    name: nodeName,
    group: "card addTarget",
    content,

    draggable: true,

    addAttributes() {
      return {
        id: {
          ...renderStringAsData("id"),
          isRequired: true,
        },
        parentCardId: {
          ...renderStringAsData("parentCardId"),
          default: null,
        },
        commentIds: {
          ...renderJsonAsData("commentIds"),
          default: [],
        },
        isPublic: {
          ...renderBooleanAsData("isPublic", "is-public"),
          default: false,
        },
      }
    },

    renderHTML({ HTMLAttributes }) {
      return ["div", mergeAttributes(HTMLAttributes, { "data-content-type": nodeName }), 0]
    },

    parseHTML() {
      return [
        {
          tag: `div[data-content-type="${nodeName}"]`,
        },
      ]
    },

    addKeyboardShortcuts() {
      return {
        Enter: () => handleEnter(this.editor, nodeName),
        ...(nodeName === "titledCard"
          ? {
              Backspace: () => cancelBackspaceAtContentStart(this.editor, this.name),
              "Shift-Backspace": () => cancelBackspaceAtContentStart(this.editor, this.name),
              "Mod-Backspace": () => cancelBackspaceAtContentStart(this.editor, this.name),
              "Alt-Backspace": () => cancelBackspaceAtContentStart(this.editor, this.name),
            }
          : {}),
      }
    },

    addNodeView() {
      return ReactNodeViewRenderer(CardExtensionComponent)
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          props: {
            transformCopied: transformCopiedFactory(nodeName),
            transformPasted: transformPastedFactory(nodeName),
          },
        }),
      ]
    },
  })
}

/**
 * Handle pressing enter when the selection is a card NodeSelection.
 *
 * By default, nothing happens. Instead move the cursor to the end of the card.
 */
function handleEnter(editor: Editor, nodeName: string): boolean {
  const { state } = editor
  const { selection } = state

  if (selection instanceof NodeSelection && selection.node.type.name === nodeName) {
    editor.commands.command(({ tr }) => {
      // Move the cursor to the end of the card.
      // If it ends in a widget, use a GapCursor.
      const $contentEndPos = tr.doc.resolve(selection.to - 1)
      const lastBlock = selection.node.lastChild
      if (lastBlock?.type.isInGroup("widget")) {
        tr.setSelection(new GapCursor($contentEndPos))
      } else {
        tr.setSelection(TextSelection.near($contentEndPos))
      }
      tr.scrollIntoView()
      return true
    })

    return true
  }

  return false
}

/**
 * If the copied content is wrapped in a card but doesn't actually contain a full card,
 * unwrap it.
 *
 * The wrapping card confuses tiptap-extension-flat-list's pastePlugin, which expects a top-level list.
 */
function transformCopiedFactory(nodeName: string) {
  return function transformCopied(slice: Slice): Slice {
    // A whole card copied as itself, not a cut that landed inside one. Unwrapping it would
    // strip its id and comments — and an untitledCard, having no cardTitle to be recognized
    // by, matches the check below on every copy.
    if (wholeNodeOf(slice) !== null) return slice

    if (slice.content.childCount === 1 && slice.content.firstChild!.type.name === nodeName) {
      const cardContent = slice.content.firstChild!.content
      if (!(cardContent.firstChild?.type.name === "cardTitle")) {
        return new Slice(cardContent, slice.openStart - 1, slice.openEnd - 1)
      }
    }

    return slice
  }
}

/**
 * When pasting titledCard cards, assign each a fresh id and store the original as
 * parentCardId.
 * Also erase commentIds, to avoid duplicating comments.
 */
function transformPastedFactory(nodeName: string) {
  return function transformPasted(slice: Slice): Slice {
    // A move relocates a card that already exists; only a copy makes a second one.
    if (isMoveDrop()) return slice

    const replaceCards = (fragment: Fragment): Fragment => {
      const children: ReturnType<typeof fragment.child>[] = []
      for (let i = 0; i < fragment.childCount; i++) {
        const node = fragment.child(i)
        if (node.type.name === nodeName) {
          const attrs = node.attrs as CardExtensionAttrs
          const newAttrs = {
            ...attrs,
            id: ObjectId.create(),
            parentCardId: attrs.id,
            commentIds: [],
          } satisfies CardExtensionAttrs
          children.push(node.type.create(newAttrs, replaceCards(node.content), node.marks))
        } else {
          children.push(node.copy(replaceCards(node.content)))
        }
      }
      return Fragment.fromArray(children)
    }

    return new Slice(replaceCards(slice.content), slice.openStart, slice.openEnd)
  }
}
